import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * El lado *app* del canal de vista previa de Xurvey.
 *
 * **La fuente de verdad del contrato es
 * `ox-xurvey/src/contracts/preview-channel.ts`**; esto es su espejo. Se repite
 * en vez de compartirse por paquete porque un paquete npm entre las dos apps
 * es exactamente el acoplamiento del que este ecosistema lleva un refactor
 * entero saliendo: obligaría a versionar y publicar para cambiar un mensaje, y
 * a que las dos fueran a la vez. Son cinco líneas de tipos; la duplicación
 * sale más barata que la correa.
 *
 * Cuando se toque el contrato, se tocan los dos ficheros. Están enlazados por
 * nombre a propósito para que buscar uno encuentre el otro.
 */

export type PreviewCommand =
  | { type: 'xurvey:preview/goToStep'; stepId: number }
  | {
      type: 'xurvey:preview/overrides';
      design?: Record<string, unknown>;
      styleOverrides?: Record<string, string>;
      hiddenStepIds?: number[];
      /** Preguntas ocultas dentro de pasos que sí se ven. */
      hiddenComponentIds?: number[];
      stepOrder?: number[];
      locale?: string;
      /**
       * El juego de ESTE despliegue. `null` = el que traiga la encuesta.
       *
       * No está en `overrides` —vive en su propia columna del despliegue— pero
       * viaja por el mismo canal para que la previa lo enseñe al elegirlo, sin
       * esperar a guardar y recargar.
       */
      gameCode?: string | null;
      /** Los idiomas que este despliegue ofrece. Con uno, no hay selector. */
      locales?: string[];
      /** El botón bajo el QR del premio. `''` lo quita. */
      prizeLinkUrl?: string;
      prizeLinkLabel?: string;
      prizeLinkStyle?: 'link' | 'button' | 'iconButton';
      /** La imagen de la bienvenida de este evento. Se funde clave a clave. */
      welcome?: { imageUrl?: string; imageRole?: 'hero' | 'logo' | 'background' };
    };

/** Un paso tal como lo declara la previa, con sus preguntas. */
export interface PasoPrevia {
  id: number;
  name: string;
  hasGame: boolean;
  /**
   * Las preguntas del paso, SIN filtrar por lo que este despliegue oculte: si
   * llegaran ya filtradas, lo ocultado desaparecería de la lista y no habría
   * forma de volver a enseñarlo.
   */
  components: Array<{
    id: number;
    label: string;
    type: string;
    required: boolean;
    /**
     * Sólo en `consentForm`: ese paso pide además el consentimiento de imagen.
     * Lo declara la previa porque el `config` del componente no llega por aquí,
     * y sin él no habría forma de saber qué texto de la marca hace falta.
     */
    showPhotoConsent?: boolean;
  }>;
}

export type PreviewEvent =
  | {
      type: 'xurvey:preview/ready';
      /** `hasGame`: a qué paso ofrecerle elegir juego. Lo dice la previa. */
      steps: PasoPrevia[];
      /** Lo que la encuesta trae de serie, ya resuelto. Ver el contrato. */
      tokens: Record<string, string>;
      design: Record<string, unknown>;
    }
  | { type: 'xurvey:preview/step'; stepId: number; index: number };

/**
 * Habla con la previa embebida: le manda órdenes y escucha lo que pasa dentro.
 *
 * Devuelve los pasos que la propia previa declara, no los que la app cree que
 * hay. Es deliberado: si la lista de la izquierda saliera de la API y el móvil
 * de su bundle, un paso archivado entre medias aparecería en una y no en el
 * otro, y nadie sabría cuál de las dos miente.
 */
export function usePreviewFrame(origenPrevia: string, clave: string | null) {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [listo, setListo] = useState(false);
  const [pasos, setPasos] = useState<PasoPrevia[]>([]);
  /*
   * Lo que la encuesta trae de serie.
   *
   * Es lo que el panel enseña en gris para lo que nadie ha tocado. Sin esto, un
   * `input[type=color]` sin valor se pinta negro, y el panel decía «negro»
   * mientras el móvil de al lado pintaba verde.
   */
  const [heredado, setHeredado] = useState<{
    tokens: Record<string, string>;
    design: Record<string, unknown>;
  }>({ tokens: {}, design: {} });
  const [pasoActual, setPasoActual] = useState<number | null>(null);
  /*
   * Lo último que se quiso mandar antes de que la previa dijera «listo».
   *
   * Sin esto, el primer color que alguien toca mientras el iframe todavía
   * carga se pierde: se manda, no lo oye nadie, y el panel y el móvil quedan
   * diciendo cosas distintas hasta el siguiente cambio.
   */
  const pendiente = useRef<PreviewCommand[]>([]);

  const enviar = useCallback(
    (cmd: PreviewCommand) => {
      const ventana = frame.current?.contentWindow;
      if (!ventana || !listo) {
        pendiente.current.push(cmd);
        return;
      }
      ventana.postMessage(cmd, origenPrevia);
    },
    [listo, origenPrevia],
  );

  /*
   * Al cambiar de encuesta, el iframe se remonta y **todo lo de la anterior
   * deja de valer**: sus pasos, el paso en el que estaba y el «ya se puede
   * hablar».
   *
   * Sin esto quedaba lo peor de los dos mundos: la lista seguía enseñando los
   * pasos de la encuesta vieja, y los cambios de diseño se mandaban a una
   * ventana recién creada que todavía no escuchaba, así que se perdían.
   */
  useEffect(() => {
    setListo(false);
    setPasos([]);
    setHeredado({ tokens: {}, design: {} });
    setPasoActual(null);
    pendiente.current = [];
  }, [clave]);

  useEffect(() => {
    const onMensaje = (e: MessageEvent) => {
      // Del iframe de la previa y de nadie más.
      if (e.origin !== origenPrevia) return;
      if (frame.current && e.source !== frame.current.contentWindow) return;
      const data = e.data as PreviewEvent | undefined;
      if (!data || typeof data.type !== 'string' || !data.type.startsWith('xurvey:preview')) return;

      if (data.type === 'xurvey:preview/ready') {
        // `components` puede faltar si la previa que responde es de un build
        // anterior a esta función: sin el `?? []`, la lista de la izquierda
        // reventaría al pintar las preguntas de un paso.
        setPasos(data.steps.map((s) => ({ ...s, components: s.components ?? [] })));
        setHeredado({ tokens: data.tokens ?? {}, design: data.design ?? {} });
        setListo(true);
      }
      if (data.type === 'xurvey:preview/step') setPasoActual(data.stepId);
    };
    window.addEventListener('message', onMensaje);
    return () => window.removeEventListener('message', onMensaje);
  }, [origenPrevia]);

  // Al decir «listo», se le suelta todo lo que se quiso mandar mientras cargaba.
  useEffect(() => {
    if (!listo) return;
    const ventana = frame.current?.contentWindow;
    if (!ventana) return;
    for (const cmd of pendiente.current) ventana.postMessage(cmd, origenPrevia);
    pendiente.current = [];
  }, [listo, origenPrevia]);

  return { frame, listo, pasos, heredado, pasoActual, enviar, setPasoActual };
}
